import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Play, Globe, PhoneCall, TrendingUp, Clock } from "lucide-react";
import { scrollToId } from "../lib/site";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const METRICS = [
  { icon: PhoneCall, value: "0", label: "Missed calls, ever" },
  { icon: Clock, value: "24/7", label: "AI answering & booking" },
  { icon: Globe, value: "48h", label: "Free preview turnaround" },
  { icon: TrendingUp, value: "3x", label: "More captured bookings" },
];

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 40);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 40);
  };

  return (
    <section
      id="hero"
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden circuit-grid"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 h-[560px] w-[560px] rounded-full bg-cyan-500/12 blur-[140px]" />
        <div className="absolute top-1/3 -right-48 h-[620px] w-[620px] rounded-full bg-violet-600/15 blur-[160px]" />
      </motion.div>

      <motion.div
        style={{ x: sx, y: sy }}
        className="absolute right-[6%] top-1/2 -translate-y-1/2 hidden lg:block pointer-events-none"
        data-testid="hero-orb"
      >
        <div className="relative h-[420px] w-[420px]">
          <div className="orb-ring absolute inset-0 rounded-full border border-cyan-400/25" />
          <div className="orb-ring-rev absolute inset-8 rounded-full border border-dashed border-violet-500/30" />
          <div className="orb-ring absolute inset-16 rounded-full border border-cyan-400/15" style={{ animationDuration: "40s" }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-40 w-40 rounded-full bg-gradient-to-br from-cyan-400/30 to-violet-600/30 blur-2xl pulse-ring" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-24 w-24 rounded-full border border-cyan-300/50 bg-[#0D111A]/80 backdrop-blur flex items-center justify-center shadow-[0_0_60px_rgba(0,240,255,0.35)]">
              <PhoneCall className="h-9 w-9 text-cyan-300" />
            </div>
          </div>
          {[0, 90, 180, 270].map((deg) => (
            <div
              key={deg}
              className="orb-ring absolute inset-0"
              style={{ animationDuration: "24s" }}
            >
              <div
                className="absolute h-2 w-2 rounded-full bg-cyan-300 shadow-[0_0_12px_rgba(0,240,255,0.9)]"
                style={{
                  top: "50%",
                  left: "50%",
                  transform: `rotate(${deg}deg) translateX(210px) translateY(-50%)`,
                  transformOrigin: "0 0",
                }}
              />
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 w-full">
        <MaskedLine delay={0.35}>
          <span className="inline-flex items-center gap-2.5 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-4 py-2 text-xs sm:text-sm font-mono font-semibold tracking-[0.22em] uppercase text-cyan-300">
            <span className="h-1.5 w-1.5 rounded-full bg-cyan-300 shadow-[0_0_10px_rgba(0,240,255,1)]" />
            TalkLogical — AI for local business
          </span>
        </MaskedLine>

        <h1 className="mt-8 font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-6xl lg:text-7xl xl:text-[5.2rem] max-w-4xl">
          <MaskedLine delay={0.5}>AI builds your website.</MaskedLine>
          <MaskedLine delay={0.65}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-cyan-400 to-violet-400 text-glow-cyan">
              AI answers your phone.
            </span>
          </MaskedLine>
          <MaskedLine delay={0.8}>You never miss a customer.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
        >
          TalkLogical crafts stunning websites from your Google Business Profile and deploys an
          autonomous AI voice agent that books, answers, and dispatches — around the clock.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-free-preview"
            onClick={() => scrollToId("preview")}
            className="group inline-flex items-center gap-2.5 rounded-full bg-cyan-400 px-7 py-4 text-base font-bold text-[#07090E] hover:bg-cyan-300 hover:shadow-[0_0_44px_rgba(0,240,255,0.5)] transition-[background-color,box-shadow] duration-300"
          >
            Get my free website preview
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-voice-demo"
            onClick={() => scrollToId("voice-demo")}
            className="group inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 backdrop-blur px-7 py-4 text-base font-semibold text-slate-200 hover:border-violet-400/60 hover:text-white transition-[border-color,color] duration-300"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-500/20 ring-1 ring-violet-400/50 group-hover:bg-violet-500/35 transition-colors">
              <Play className="h-3.5 w-3.5 text-violet-300 fill-violet-300" />
            </span>
            Hear the AI agent
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl border border-white/10 bg-white/10 overflow-hidden max-w-3xl"
          data-testid="hero-metrics"
        >
          {METRICS.map((m) => (
            <div key={m.label} className="bg-[#0D111A]/90 backdrop-blur px-5 py-5">
              <m.icon className="h-4 w-4 text-cyan-400 mb-2.5" />
              <p className="font-display text-2xl font-bold text-slate-50">{m.value}</p>
              <p className="mt-1 text-xs text-slate-500 font-medium tracking-wide">{m.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
